"use client";

import { useState } from "react";
import OfferCard from "@/app/components/offers/offer-card";
import ListPriceSavingsBadge from "@/app/components/offers/list-price-savings-badge";
import CampaignCard from "@/app/(pages)/(frontend)/t/[tenant]/aktionen/components/campaign-card";
import FallbackMessage from "@/app/components/ui/fallback-message";
import CardFilters from "@/app/(pages)/(frontend)/t/[tenant]/aktionen/components/card-filters";
import {
  getConsumptionText,
  getEfficiencyLabel,
  getHybridEfficiencyLabels,
  getPriceText,
} from "@/app/utils/helpers/backend/offers/offer-card/offer-card-helpers";
import { sortOffersByClassificationMakeAndPrice } from "@/app/utils/helpers/backend/offers/sort-offers";
import { sortCampaignsBySortIndexAndCreatedAt } from "@/app/utils/helpers/backend/campaigns/sort-campaigns";
import { getOfferIdsFromCampaigns } from "@/app/utils/helpers/backend/campaigns/get-offer-ids-from-campaigns";
import { getCampaignTypeLabels } from "@/app/utils/helpers/backend/campaigns/get-campaign-type-labels";
import type { OfferFormDataT } from "@/app/types/offers";
import type { CampaignWithCampTypesAndLocationsT } from "@/app/types/campaigns";
import type { PublishedMakeT } from "@/app/services/server/frontend/get-published-makes";
import type { PublishedOfferTypeT } from "@/app/services/server/frontend/get-published-offer-types";
import type { PublishedCampaignTypeT } from "@/app/services/server/frontend/get-published-campaign-types";
import { buildBrandPath, type BrandKeyT } from "@/app/config/brand-registry";

interface FilteredCardSectionProps {
  offers: OfferFormDataT[];
  campaigns: CampaignWithCampTypesAndLocationsT[];
  makes: PublishedMakeT[];
  offerTypes: PublishedOfferTypeT[];
  campaignTypes: PublishedCampaignTypeT[];
  brandKey?: BrandKeyT;
}

/**
 * Filtered card section component for displaying offers and campaigns with filtering.
 * Supports filtering by brand (make) and type (offer/campaign types).
 */
export default function FilteredCardSection({
  offers,
  campaigns,
  makes,
  offerTypes,
  campaignTypes,
  brandKey = "default",
}: FilteredCardSectionProps) {
  const [selectedBrand, setSelectedBrand] = useState<string>("all");
  const [selectedType, setSelectedType] = useState<string>("all");

  // Collect all offer IDs that are assigned to campaigns
  const offerIdsInCampaigns = getOfferIdsFromCampaigns(campaigns);

  // Filter offers by brand and type, excluding offers assigned to campaigns
  const filteredOffers = offers.filter((offer) => {
    // Exclude offers that are assigned to a campaign
    if (offerIdsInCampaigns.has(offer.id)) {
      return false;
    }

    const brandMatch =
      selectedBrand === "all" || offer.make?.name === selectedBrand;
    const typeMatch =
      selectedType === "all" ||
      offer.offer_types?.some(
        (type) => type.label.toLowerCase() === selectedType.toLowerCase()
      );
    return brandMatch && typeMatch;
  });

  // Filter campaigns by brand (via related_offers) and type (via campaign_types)
  const filteredCampaigns = campaigns.filter((campaign) => {
    // Brand filtering: Check if any related offer matches the selected brand
    const brandMatch =
      selectedBrand === "all" ||
      campaign.related_offers?.some(
        (offer) => offer.make?.name === selectedBrand
      );

    // Type filtering: Check if campaign has matching campaign_type
    const typeMatch =
      selectedType === "all" ||
      campaign.campaign_types?.some(
        (type) => type.label.toLowerCase() === selectedType.toLowerCase()
      );

    return brandMatch && typeMatch;
  });

  // Sort campaigns: first by sort_index (if set), then by created_at
  const sortedCampaigns =
    sortCampaignsBySortIndexAndCreatedAt(filteredCampaigns);

  // Sort offers: first private, then business; within each group: by make, then by price ascending
  const sortedOffers = sortOffersByClassificationMakeAndPrice(filteredOffers);

  const hasResults = sortedOffers.length > 0 || sortedCampaigns.length > 0;

  return (
    <>
      <CardFilters
        selectedBrand={selectedBrand}
        selectedType={selectedType}
        onBrandChange={setSelectedBrand}
        onTypeChange={setSelectedType}
        makes={makes}
        offerTypes={offerTypes}
        campaignTypes={campaignTypes}
      />

      {!hasResults ? (
        <FallbackMessage
          title="Keine Angebote gefunden."
          description="Bitte versuchen Sie einen anderen Filter."
          className="mt-8"
        />
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-6 md:mt-12 md:grid-cols-2 lg:grid-cols-3">
          {sortedCampaigns.map((campaign) => {
            const displayTypes = getCampaignTypeLabels(campaign.campaign_types);

            return (
              <CampaignCard
                key={`campaign-${campaign.id}`}
                image={campaign.thumbnail_image?.url}
                title={campaign.title}
                subtitle={campaign.sub_title || ""}
                campaignTypes={displayTypes}
                href={buildBrandPath(brandKey, `/aktionen/${campaign.slug}`)}
              />
            );
          })}
          {sortedOffers.map((offer) => {
            const displayTypes =
              offer?.offer_types?.map((type) => type.label) || [];

            return (
              <OfferCard
                key={`offer-${offer.id}`}
                image={offer.thumbnail_image?.url || "/images/eyeoff.svg"}
                title={offer.title || ""}
                efficiency={getConsumptionText(offer)}
                price={getPriceText(offer)}
                efficiencyLabel={getEfficiencyLabel(offer)}
                hybridLabels={getHybridEfficiencyLabels(offer)}
                offer_types={displayTypes}
                listPriceSavingsBadge={
                  <ListPriceSavingsBadge offer={offer} footnoteMarker="*" />
                }
                href={buildBrandPath(brandKey, `/angebote/${offer.slug}`)}
              />
            );
          })}
        </div>
      )}
    </>
  );
}
